import { z } from 'zod';

import { type MalformedResponse, type ValidationFailure } from '@lib/api/errors';
import { Either } from '@lib/monad';

const $s = z.string();
const $b = z.boolean();

const $ValidationResponse = z.object({
    valid: $b,
    validation_results: z.array(
        z.object({
            member_names: z.array($s).optional(),
            error_message: $s
        })
    )
});

type ValidationResponse = z.infer<typeof $ValidationResponse>;

export type ValidationResult<T> = Either<ValidationFailure | MalformedResponse, T>;

export const makeApiParser =
    <S extends z.ZodType>($schema: S) =>
    (payload: unknown): ValidationResult<z.infer<typeof $schema>> => {
        const parsed = $ValidationResponse.safeParse(payload);
        if (!parsed.success) {
            return Either.left({
                systemErrors: parsed.error,
                recovery: payload
            });
        }

        const validation = parsed.data;

        if (!validation.valid) {
            return Either.left({
                errors: flattenValidationResponse(validation)
            });
        }

        const apiResponse = $schema.safeParse(payload);

        if (apiResponse.success) {
            // zod 4 types a generic schema's output as `unknown` (zod 3 used `any`).
            return Either.right(apiResponse.data as NonNullable<z.infer<S>>);
        }

        return Either.left({
            systemErrors: apiResponse.error,
            recovery: payload
        });
    };

const flattenValidationResponse = (validationResponse: ValidationResponse) => {
    return validationResponse.validation_results?.map(({ member_names, error_message }) => {
        return {
            code: 'custom',
            path: member_names ?? [''],
            message: error_message
        };
    });
};
